import type {ReactElement} from 'react';
import {Text, TextSize} from '@Components/text';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {VisuallyHiddenText} from '@Components/visually-hidden-text/visually-hidden-text';
import {BrandKitSwatchCluster} from '@Components/brand-kit-swatch-cluster/brand-kit-swatch-cluster';
import {BrandKitSwatchClusterVariant} from '@Components/brand-kit-swatch-cluster/brand-kit-swatch-cluster.types';
import type {ProposedChangeValue} from '@Components/ai-chat-window/components/proposed-change-card/proposed-change-card.types';
import type {ProposedChangeRowProps} from './proposed-change-row.types';
import styles from './proposed-change-row.module.scss';

/** One "label: before → after" line. The swatch is decorative: the value's text already names the colour. */
export function ProposedChangeRow({change, isStale}: ProposedChangeRowProps): ReactElement {
  const renderSwatch = (value: ProposedChangeValue): ReactElement | null => {
    if (value.swatchHex === null) {
      return null;
    }

    return <BrandKitSwatchCluster colorHexes={[value.swatchHex]} variant={BrandKitSwatchClusterVariant.COMPACT} />;
  };

  const renderValue = (value: ProposedChangeValue): ReactElement => {
    if (value.imageUrl !== null) {
      return <img className={styles.proposedChangeThumbnail} src={value.imageUrl} alt={value.text} />;
    }

    return (
      <span className={styles.proposedChangeValue}>
        {renderSwatch(value)}
        <Text val={value.text} size={TextSize.MD} className={styles.proposedChangeValueText} />
      </span>
    );
  };

  const renderWasLabel = (): ReactElement | null => {
    if (!isStale) {
      return null;
    }

    return <Text val={window.i18next.t('pmwjs_ai_proposed_change_was')} size={TextSize.SM} className={styles.proposedChangeWasLabel} />;
  };

  const renderBefore = (): ReactElement | null => {
    if (change.before === null) {
      return null;
    }

    return (
      <span className={styles.proposedChangeBefore}>
        {renderWasLabel()}
        {renderValue(change.before)}
      </span>
    );
  };

  const renderArrow = (): ReactElement | null => {
    if (change.before === null) {
      return null;
    }

    return (
      <>
        <Icon icon="icon-arrow-right" size={IconSize.SIZE_ICON_16} className={styles.proposedChangeArrow} ariaHidden />
        <VisuallyHiddenText val={window.i18next.t('pmwjs_ai_proposed_change_to')} />
      </>
    );
  };

  return (
    <div className={styles.proposedChangeRow}>
      <dt className={styles.proposedChangeLabel}>
        <Text val={change.label} size={TextSize.SM} bold />
      </dt>
      <dd className={styles.proposedChangeValues}>
        {renderBefore()}
        <span className={styles.proposedChangeAfter}>
          {renderArrow()}
          {renderValue(change.after)}
        </span>
      </dd>
    </div>
  );
}
